"use client";

import { motion, AnimatePresence } from 'motion/react';
import { X, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type ImageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  src: string;
  alt: string;
};

export const ImageModal = ({ isOpen, onClose, src, alt }: ImageModalProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (isOpen) {
      setImageLoaded(false);
      setImageError(false);
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  if (!mounted) return null;

  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Semi-transparent Backdrop (clickable) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-md cursor-pointer"
            onClick={onClose}
          />

          {/* Modal Content container */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full h-full flex flex-col items-center justify-center p-2 sm:p-4 pointer-events-none"
          >
            {/* Top Bar - Close Button */}
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50 pointer-events-auto">
              <button
                onClick={onClose}
                className="group p-2 sm:p-3 text-white transition-all bg-black/50 hover:bg-black/70 rounded-full backdrop-blur-xl border border-white/20 hover:scale-110 active:scale-95 shadow-2xl"
                aria-label="Close modal"
              >
                <X className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            </div>

            {/* Main Visual Container - Balanced size to avoid button overlap */}
            <div className="relative w-full h-full flex items-center justify-center pointer-events-none p-4 sm:p-8">
              <div className="relative w-[85vw] h-[75vh] flex items-center justify-center pointer-events-auto transition-all duration-300">
                {/* Loading State */}
                {!imageLoaded && !imageError && (
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-12 h-12 animate-spin text-white/50" />
                  </div>
                )}

                {/* Error State */}
                {imageError && (
                  <div className="flex flex-col items-center justify-center bg-black/40 backdrop-blur-xl text-white/70 p-12 rounded-3xl border border-white/20 text-center pointer-events-auto">
                    <div className="text-5xl mb-4">📷</div>
                    <p className="text-lg font-bold">Image Unavailable</p>
                    <button
                      onClick={onClose}
                      className="mt-6 px-6 py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-bold transition-all border border-white/20"
                    >
                      Close Preview
                    </button>
                  </div>
                )}

                {/* The Image - Sized to be prominent but not overlap UI controls */}
                {!imageError && (
                  <img
                    src={src}
                    alt={alt}
                    className={`w-full h-full block transition-all duration-500 object-contain selection:bg-transparent ${imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                      }`}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                )}
              </div>
            </div>

            {/* Title / Footer */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-4 sm:mt-6 pointer-events-auto"
            >
              <div className="inline-flex items-center gap-3 px-4 py-2 bg-black/50 backdrop-blur-md rounded-full border border-white/20 shadow-xl">
                <span className="text-white text-xs sm:text-sm font-bold tracking-wide">
                  {alt}
                </span>
                <div className="w-1 h-1 bg-white/40 rounded-full" />
                <span className="text-white/60 text-[10px] sm:text-xs">
                  Esc to close
                </span>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};
